import React, { useEffect, useRef } from 'react'
import Snackbar from '@mui/material/Snackbar'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useAppSelector, useAppDispatch } from '../hooks'
import { removeRichNotification } from '../stores/RichNotificationStore'
import { SEVV_MODAL } from './VirtualRoom/VirtualRoomModalStyles'
import { zIndexLayers } from '../MuiTheme'

const TOAST_Z = zIndexLayers.topLevel

export default function RichNotificationToast() {
  const dispatch = useAppDispatch()
  const notifications = useAppSelector((s) => s.richNotification.notifications)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const current = notifications[0] ?? null

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (!current) return

    // Only auto-dismiss when an explicit duration is set; default is persistent
    const duration = current.autoHideDuration
    if (duration && duration > 0) {
      timerRef.current = setTimeout(() => {
        dispatch(removeRichNotification(current.id))
      }, duration)
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [current, dispatch])

  if (!current) return null

  const handleDismiss = () => {
    dispatch(removeRichNotification(current.id))
  }

  return (
    <Snackbar
      open
      onClose={(_event, reason) => {
        if (reason === 'clickaway') return
        handleDismiss()
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ zIndex: TOAST_Z, mt: 2, mr: 2 }}
    >
      <Paper elevation={0} sx={{ ...SEVV_MODAL.paper, minWidth: 320, maxWidth: 400, p: 0, backdropFilter: 'none' }}>
        <Box sx={{ ...SEVV_MODAL.title, padding: '12px 16px' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {current.title}
          </Typography>
        </Box>
        <Box sx={{ padding: '16px 16px' }}>
          {current.lines.map((line, i) => (
            <Typography
              key={i}
              variant="body2"
              fontWeight={line.bold ? 600 : undefined}
              color={line.secondary ? 'text.secondary' : 'text.primary'}
              sx={line.bold ? { mb: 0.5 } : undefined}
            >
              {line.text}
            </Typography>
          ))}
        </Box>
        <Box sx={{ ...SEVV_MODAL.actions, padding: '10px 16px', display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleDismiss} sx={SEVV_MODAL.btnSecondary} size="small">
            Dismiss
          </Button>
        </Box>
      </Paper>
    </Snackbar>
  )
}
